import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { UNIDADES, getUnidad } from "../../lib/unidades";
import { ArrowRight, TrendingUp, Calendar, FileText, FileSignature, CreditCard, AlertCircle, Plus, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, valor, hint, color }) => (
  <div className="eo-card p-4">
    <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
    <p className="font-display text-2xl mt-1" style={{ color: color || "var(--brand-text)" }}>{valor}</p>
    {hint && <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>{hint}</p>}
  </div>
);

const $ = (n) => `$${(Math.round(n || 0)).toLocaleString("es-MX")}`;

// =================== DASHBOARD GENERAL ===================
export const DashboardGeneral = () => {
  const [r, setR] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { axios.get(`${API}/unidades/resumen`).then(x => setR(x.data)); }, []);
  if (!r) return <div className="p-6 text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div>;
  const g = r.global;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl" style={{ color: "var(--brand-text)" }}>Dashboard General · Celebra Group</h1>
        <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>Vista de dirección · KPIs de las 3 unidades de negocio.</p>
      </div>

      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Ventas del mes" valor={$(g.ventas_totales_mes)} color="#16a34a" />
        <Stat label="Eventos próximos" valor={g.eventos_proximos} />
        <Stat label="Cotizaciones pendientes" valor={g.cotizaciones_pendientes} />
        <Stat label="Contratos pendientes" valor={g.contratos_pendientes} />
        <Stat label="Clientes registrados" valor={g.clientes_total} />
      </div>

      {/* Accesos por unidad */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {UNIDADES.map(u => {
          const s = r.unidades[u.slug] || {};
          return (
            <Link key={u.slug} to={u.panel_path} className="eo-card eo-card-hover p-5 block" data-testid={`acceso-${u.slug}`} style={{ borderTop: `4px solid ${u.color}` }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: u.color_soft }}>
                  <u.icon className="w-5 h-5" style={{ color: u.color }} />
                </div>
                <div className="flex-1">
                  <p className="font-display text-lg" style={{ color: u.color }}>{u.nombre}</p>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{u.descripcion_corta}</p>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: u.color }} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
                  <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Eventos próximos</p>
                  <p className="font-display text-xl" style={{ color: u.color_dark }}>{s.eventos_proximos || 0}</p>
                </div>
                <div className="p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
                  <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Ventas mes</p>
                  <p className="font-display text-xl" style={{ color: u.color_dark }}>{$(s.ventas_mes)}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Acciones rápidas */}
      <div className="eo-card p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--brand-text-muted)" }}>Acciones rápidas</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/panel/cotizar")} className="eo-btn eo-btn-primary" data-testid="btn-nueva-cotizacion"><Plus className="w-4 h-4" /> Nueva cotización</button>
          <button onClick={() => navigate("/panel/clientes")} className="eo-btn eo-btn-outline">Nuevo cliente</button>
          <button onClick={() => navigate("/panel/agenda")} className="eo-btn eo-btn-outline"><Calendar className="w-4 h-4" /> Calendario general</button>
          <button onClick={() => navigate("/panel/contratos")} className="eo-btn eo-btn-outline"><FileSignature className="w-4 h-4" /> Contratos</button>
          <button onClick={() => navigate("/panel/finanzas-pro")} className="eo-btn eo-btn-outline"><TrendingUp className="w-4 h-4" /> Finanzas</button>
        </div>
      </div>
    </div>
  );
};

// =================== DASHBOARD POR UNIDAD (Petit / Kermesita / Bocatto) ===================
export const DashboardUnidad = ({ slug }) => {
  const u = getUnidad(slug);
  const [r, setR] = useState(null);
  const [petitFechas, setPetitFechas] = useState([]);
  const [mesActivo, setMesActivo] = useState(new Date().toISOString().substring(0, 7));
  const navigate = useNavigate();
  const Icon = u?.icon;

  useEffect(() => {
    axios.get(`${API}/unidades/resumen`).then(x => setR(x.data));
  }, [slug]);

  useEffect(() => {
    if (slug === "petit") {
      axios.get(`${API}/unidades/petit/disponibilidad?mes=${mesActivo}`).then(x => setPetitFechas(x.data.fechas || []));
    }
  }, [slug, mesActivo]);

  const cambiarMes = (delta) => {
    const [y, m] = mesActivo.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMesActivo(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const onClickDia = (fechaStr, fechaInfo) => {
    // Click en fecha ocupada (cualquier estado) → abrir expediente del evento que la bloquea
    const estadosOcupados = ["contratada", "apartada", "en_produccion", "en_evento", "finalizada"];
    if (fechaInfo && estadosOcupados.includes(fechaInfo.estado) && fechaInfo.evento_id) {
      navigate(`/panel/eventos/${fechaInfo.evento_id}`);
      return;
    }
    if (fechaInfo && fechaInfo.estado === "bloqueada") {
      return; // bloqueo manual, sin acción
    }
    // Fecha libre → wizard de nuevo evento con fecha pre-cargada
    navigate(`/panel/nuevo-evento?fecha=${fechaStr}&unidad=petit`);
  };

  if (!u) return <div>Unidad desconocida</div>;
  const s = r?.unidades?.[slug] || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: u.color_soft }}>
            {Icon && <Icon className="w-6 h-6" style={{ color: u.color }} />}
          </div>
          <div>
            <h1 className="font-display text-3xl" style={{ color: u.color }}>{u.nombre}</h1>
            <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>{u.descripcion_corta}</p>
          </div>
        </div>
        <Link to="/panel/dashboard" className="text-xs" style={{ color: u.color }}>← Ir al Dashboard General</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Eventos próximos" valor={s.eventos_proximos || 0} color={u.color} />
        <Stat label="Ventas del mes" valor={$(s.ventas_mes)} color={u.color} />
        <Stat label="Contratos activos" valor={s.contratos_activos || 0} />
        <Stat label="Cotizaciones pendientes" valor={s.cotizaciones_pendientes || 0} />
      </div>

      {/* Calendario Petit con disponibilidad */}
      {slug === "petit" && (
        <div className="eo-card p-5" data-testid="petit-calendario">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <p className="font-display text-lg" style={{ color: u.color }}>Calendario de reservas — 1 evento por día</p>
            <div className="flex items-center gap-2">
              <button onClick={() => cambiarMes(-1)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium px-2 capitalize min-w-[140px] text-center">
                {(() => {
                  const [yy, mm] = mesActivo.split("-").map(Number);
                  return new Date(yy, mm - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
                })()}
              </span>
              <button onClick={() => cambiarMes(1)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex gap-4 text-[10px] mb-3 flex-wrap">
            {[
              { lbl: "Disponible (click para crear)", color: "#f5f5f4" },
              { lbl: "Apartada", color: "#fbbf24" },
              { lbl: "Contratada", color: u.color },
              { lbl: "Bloqueada", color: "#94a3b8" },
            ].map(l => <span key={l.lbl} className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: l.color }} /> {l.lbl}</span>)}
          </div>
          {(() => {
            const [yy, mm] = mesActivo.split("-").map(Number);
            const diasEnMes = new Date(yy, mm, 0).getDate();
            const firstDow = new Date(yy, mm - 1, 1).getDay();
            return (
              <>
                <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--brand-text-muted)" }}>
                  {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(d => <span key={d} className="text-center">{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1.5 text-xs">
                  {Array.from({ length: firstDow }, (_, i) => <div key={`empty-${i}`} />)}
                  {Array.from({ length: diasEnMes }, (_, i) => i + 1).map(dia => {
                    const fechaStr = `${mesActivo}-${String(dia).padStart(2, "0")}`;
                    const f = petitFechas.find(x => x.fecha === fechaStr);
                    const bg = !f ? "var(--brand-surface-muted)" :
                      f.estado === "contratada" ? u.color :
                      f.estado === "apartada" ? "#fbbf24" :
                      f.estado === "bloqueada" ? "#94a3b8" :
                      f.estado === "cancelada" ? "#dc2626" : "#cbd5e1";
                    const fg = (f && f.estado !== "apartada") ? "#fff" : "var(--brand-text)";
                    const clickable = !f || f.estado !== "bloqueada";
                    return (
                      <button
                        key={dia}
                        onClick={() => clickable && onClickDia(fechaStr, f)}
                        disabled={!clickable}
                        className="aspect-square rounded-lg flex flex-col items-center justify-center transition hover:scale-105 hover:shadow disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ background: bg, color: fg }}
                        title={f ? `${fechaStr} · ${f.estado}${f.cliente_nombre ? " · " + f.cliente_nombre : ""}${clickable ? " · Click para abrir evento" : ""}` : `${fechaStr} · libre · Click para crear evento`}
                        data-testid={`petit-dia-${dia}`}
                      >
                        <span className="font-medium">{dia}</span>
                        {f?.cliente_nombre && <span className="text-[9px] truncate w-full px-1 text-center opacity-90">{f.cliente_nombre.split(" ")[0]}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] mt-3" style={{ color: "var(--brand-text-muted)" }}>💡 Click en una fecha libre para crear un evento ahí · Click en una fecha contratada/apartada para abrir el expediente del evento.</p>
              </>
            );
          })()}
        </div>
      )}

      {/* Próximos eventos de la unidad */}
      <div className="eo-card p-5">
        <p className="font-display text-lg mb-3" style={{ color: u.color }}>Próximos eventos</p>
        <div className="space-y-2">
          {(s.proximos_eventos || []).length === 0 && <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Sin eventos próximos.</p>}
          {(s.proximos_eventos || []).map(e => (
            <Link key={e.evento_id} to={`/panel/eventos/${e.evento_id}`} className="block p-3 rounded-xl" style={{ background: "var(--brand-surface-muted)" }}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{e.nombre}</p>
                  <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{e.cliente_nombre} · {e.fecha}</p>
                </div>
                <span className="eo-pill" style={{ background: u.color_soft, color: u.color }}>{e.estatus}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Solo el botón para compartir el link público de la unidad */}
      <div className="eo-card p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: u.color }}>Captar más clientes</p>
        <div className="flex flex-wrap gap-2">
          {slug === "petit" && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/reservar`;
                navigator.clipboard?.writeText(url);
                const msg = `🏠 Reserva tu fecha en Celebra Petit\n\nMira disponibilidad y aparta gratis por 24h:\n${url}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              className="eo-btn eo-btn-primary"
              data-testid="btn-compartir-reservar"
            >
              📲 Compartir link de reservas
            </button>
          )}
          {slug === "kermesita" && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/reservar/kermesita`;
                navigator.clipboard?.writeText(url);
                window.open(`https://wa.me/?text=${encodeURIComponent(`✨ Cotiza tu evento con La Kermesita\n\nDecoración, planeación y producción a la medida:\n${url}`)}`, "_blank");
              }}
              className="eo-btn eo-btn-primary"
              data-testid="btn-compartir-kermesita"
            >
              📲 Compartir link de cotización
            </button>
          )}
          {slug === "bocatto" && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/reservar/bocatto`;
                navigator.clipboard?.writeText(url);
                window.open(`https://wa.me/?text=${encodeURIComponent(`🍽️ Cotiza tu banquete con Bocatto\n\nCatering profesional, menús a la medida:\n${url}`)}`, "_blank");
              }}
              className="eo-btn eo-btn-primary"
              data-testid="btn-compartir-bocatto"
            >
              📲 Compartir link de catering
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const DashboardPetit = () => <DashboardUnidad slug="petit" />;
export const DashboardKermesita = () => <DashboardUnidad slug="kermesita" />;
export const DashboardBocatto = () => <DashboardUnidad slug="bocatto" />;

// =================== PANTALLA SELECCIÓN CLIENTE ===================
export const SeleccionUnidad = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: "#fdf6ee" }}>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="font-display text-4xl md:text-5xl" style={{ color: "var(--brand-text)" }}>¿Qué deseas contratar?</h1>
          <p className="text-base mt-2" style={{ color: "var(--brand-text-muted)" }}>Elige la opción que mejor se adapte a tu evento.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {UNIDADES.map(u => {
            const Icon = u.icon;
            return (
              <div key={u.slug} className="bg-white rounded-3xl overflow-hidden shadow-lg flex flex-col" data-testid={`unidad-card-${u.slug}`}>
                <div className="h-40 flex items-center justify-center" style={{ background: u.color_soft }}>
                  <Icon className="w-16 h-16" style={{ color: u.color }} />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="font-display text-2xl" style={{ color: u.color }}>{u.nombre}</p>
                  <p className="text-sm mt-1" style={{ color: "var(--brand-text)" }}>{u.descripcion_corta}</p>
                  <p className="text-xs mt-2" style={{ color: "var(--brand-text-muted)" }}>{u.descripcion_larga}</p>
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {u.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--brand-text)" }}>
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] shrink-0 mt-0.5" style={{ background: u.color }}>✓</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate(u.publico_path)} className="mt-5 w-full py-3 rounded-xl text-white font-medium" style={{ background: u.color }} data-testid={`cta-${u.slug}`}>
                    {u.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Combina servicios */}
        <div className="mt-8 bg-white rounded-3xl p-6 text-center shadow-md" data-testid="combinar-servicios">
          <p className="font-display text-xl" style={{ color: "var(--brand-text)" }}>¿Quieres combinar servicios?</p>
          <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>Puedes agregar servicios de las 3 marcas en un solo evento.</p>
          <button onClick={() => navigate("/contratar/combinado")} className="mt-4 px-6 py-3 rounded-xl text-white font-medium" style={{ background: "#4338ca" }} data-testid="cta-combinado">
            Comenzar evento combinado
          </button>
        </div>

        {/* Footer beneficios */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { t: "Fácil y rápido", d: "Cotización en minutos" },
            { t: "Seguro", d: "Tus datos protegidos" },
            { t: "Personalizado", d: "A tu medida" },
            { t: "Todo incluido", d: "Nos encargamos de todo" },
          ].map(b => (
            <div key={b.t} className="bg-white p-4 rounded-2xl">
              <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>{b.t}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--brand-text-muted)" }}>{b.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardGeneral;
